import { Header, Sidebar } from '@/components/layout';
import { type ClinicalTemplate, fetchJson } from '@/components/templates/template-utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';

interface TemplatesUserDetailProps {
  accountId: string;
}

export default function TemplatesUserDetail({ accountId }: TemplatesUserDetailProps) {
  const {
    data: templates = [],
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['templates', 'users', accountId],
    queryFn: () => fetchJson<ClinicalTemplate[]>(`/api/templates/users/${accountId}`),
  });

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Sniðmát notanda" />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-6xl space-y-6">
            <div>
              <h1 className="font-bold text-2xl text-slate-900">Sniðmát notanda</h1>
              <p className="text-muted-foreground text-sm">Reikningur: {accountId}</p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5" />
                  Persónuleg klínísk sniðmát
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : isError ? (
                  <div className="py-8 text-center text-destructive">Ekki tókst að sækja sniðmát notanda.</div>
                ) : templates.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">Engin persónuleg sniðmát fundust.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nafn</TableHead>
                        <TableHead>Lýsing</TableHead>
                        <TableHead>Uppruni</TableHead>
                        <TableHead>Stofnun</TableHead>
                        <TableHead>Uppfært</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell className="font-medium">{template.name}</TableCell>
                          <TableCell className="text-muted-foreground">{template.description || '-'}</TableCell>
                          <TableCell>
                            {template.source_template_scope ? (
                              <Badge variant="secondary">{template.source_template_scope}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">Handgert</span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{template.auth_org_id || '-'}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {template.updated_at ? new Date(template.updated_at).toLocaleDateString('is-IS') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Verkefnalisti</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Persónuleg úttekt er lesaðgerð. Global stilling notanda fyrir verkefnalista er ekki breytt hér.
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
